import { Check, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { API, MARKETS } from "../constants";
import { useLang } from "../i18n";
import { useQuote } from "../quote";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  country: "",
  customer_type: "retail",
  message: "",
};

export default function QuoteDrawer() {
  const { t, lang, isRtl } = useLang();
  const { items, setQty, remove, clear, open, closeDrawer, presetType } = useQuote();
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open && presetType) setForm((f) => ({ ...f, customer_type: presetType }));
  }, [open, presetType]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t("form.err_name"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error(t("form.err_email"));
    if (!items.length) return toast.error(t("form.err_items"));
    setSending(true);
    try {
      const res = await fetch(`${API}/quote-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, items, lang }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      clear();
    } catch {
      toast.error(t("form.err_api"));
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    closeDrawer();
    setTimeout(() => setDone(false), 400);
  };

  const x = isRtl ? "-100%" : "100%";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-[65] bg-obsidian/75 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="quote-drawer"
            initial={{ x }}
            animate={{ x: 0 }}
            exit={{ x }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 end-0 z-[70] flex w-full max-w-md flex-col border-s border-gold/20 bg-cellar"
          >
            <div className="flex items-center justify-between border-b border-gold/15 px-7 py-5">
              <h3 className="font-display text-lg tracking-[0.2em] text-gold">{t("form.title")}</h3>
              <button data-testid="quote-close-button" onClick={handleClose} aria-label={t("form.close")} className="text-parchment/70 transition-colors hover:text-gold">
                <X size={22} />
              </button>
            </div>

            {done ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-testid="quote-success">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold bg-gold/10">
                  <Check size={28} className="text-gold" />
                </span>
                <h4 className="font-display text-2xl text-parchment">{t("form.success_title")}</h4>
                <p className="max-w-xs text-sm leading-relaxed text-parchment/70">{t("form.success_body")}</p>
                <button onClick={handleClose} className="btn-outline-gold mt-4 !px-8">
                  {t("form.close")}
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-testid="quote-empty">
                <ShoppingBag size={34} className="text-gold/60" />
                <p className="text-sm text-parchment/70">{t("form.empty")}</p>
                <a href="#collection" onClick={handleClose} className="btn-outline-gold mt-2 !px-8">
                  {t("form.empty_cta")}
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-1 flex-col overflow-y-auto" data-testid="quote-form">
                <div className="border-b border-gold/15 px-7 py-6">
                  <p className="mb-4 text-[0.6rem] uppercase tracking-[0.35em] text-stone">{t("form.items_label")}</p>
                  {items.map((i) => (
                    <div key={i.slug} data-testid={`quote-item-${i.slug}`} className="mb-4 flex items-center gap-3 border border-gold/15 bg-obsidian/40 px-4 py-3">
                      <div className="flex-1">
                        <p className="font-display text-sm text-parchment">{i.name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" data-testid={`qty-minus-${i.slug}`} onClick={() => setQty(i.slug, i.qty - 1)} className="border border-gold/30 p-1 text-gold transition-colors hover:bg-gold hover:text-obsidian">
                          <Minus size={12} />
                        </button>
                        <span className="w-6 text-center text-sm text-parchment" data-testid={`qty-value-${i.slug}`}>{i.qty}</span>
                        <button type="button" data-testid={`qty-plus-${i.slug}`} onClick={() => setQty(i.slug, i.qty + 1)} className="border border-gold/30 p-1 text-gold transition-colors hover:bg-gold hover:text-obsidian">
                          <Plus size={12} />
                        </button>
                      </div>
                      <button type="button" data-testid={`remove-item-${i.slug}`} onClick={() => remove(i.slug)} aria-label={t("form.remove")} className="text-stone transition-colors hover:text-crimson">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex-1 space-y-5 px-7 py-6">
                  <input data-testid="quote-form-field-name" className="io-input" placeholder={t("form.name")} value={form.name} onChange={set("name")} />
                  <input data-testid="quote-form-field-email" className="io-input" type="email" placeholder={t("form.email")} value={form.email} onChange={set("email")} />
                  <div className="grid grid-cols-2 gap-5">
                    <input data-testid="quote-form-field-phone" className="io-input" placeholder={t("form.phone")} value={form.phone} onChange={set("phone")} />
                    <input data-testid="quote-form-field-company" className="io-input" placeholder={t("form.company")} value={form.company} onChange={set("company")} />
                  </div>
                  <select data-testid="quote-form-field-country" className="io-input" value={form.country} onChange={set("country")}>
                    <option value="">{t("form.choose")}</option>
                    {MARKETS.map((m) => (
                      <option key={m.name} value={m.name}>{m.native}</option>
                    ))}
                    <option value="other">{t("form.other")}</option>
                  </select>
                  <div>
                    <p className="mb-2 text-[0.6rem] uppercase tracking-[0.3em] text-stone">{t("form.type")}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {["retail", "wholesale", "franchise"].map((tp) => (
                        <button
                          key={tp}
                          type="button"
                          role="radio"
                          aria-checked={form.customer_type === tp}
                          data-testid={`quote-type-${tp}`}
                          onClick={() => setForm((f) => ({ ...f, customer_type: tp }))}
                          className={`flex-1 border px-3 py-2.5 text-[0.62rem] uppercase tracking-[0.2em] transition-colors duration-300 ${
                            form.customer_type === tp ? "border-gold bg-gold text-obsidian" : "border-gold/30 text-gold hover:border-gold/60"
                          }`}
                        >
                          {t(`form.${tp}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea data-testid="quote-form-field-message" className="io-input resize-none" rows={3} placeholder={t("form.message")} value={form.message} onChange={set("message")} />
                  <p className="text-xs leading-relaxed text-stone/80">{t("form.note")}</p>
                </div>

                <div className="border-t border-gold/15 px-7 py-5">
                  <button type="submit" data-testid="quote-submit-button" disabled={sending} className="btn-gold w-full disabled:opacity-50">
                    {sending ? t("form.sending") : t("form.submit")}
                  </button>
                </div>
              </form>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
